import { capture } from '@parcel/analytics/browser';
import { cn } from '@parcel/ui';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import * as React from 'react';
import { type MentionEntityKind, useProjectMentions } from '../../lib/mentions.js';
import { Hint } from '../hint.js';
import { InlineEvidence } from './inline-evidence.js';

export interface EvidenceRowItem {
  key: string;
  entityKind: MentionEntityKind;
  entityId: string;
  projectId: string;
  entityName: string;
  avatar: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  openLabel: string;
  /** The sidebar icon of the record type the row opens (Building2, Users, Tag). */
  openIcon: LucideIcon;
  onOpen: () => void;
  defaultOpen?: boolean;
}

export function ExpandableEvidenceList({
  workspaceId,
  rows,
}: { workspaceId: string | undefined; rows: EvidenceRowItem[] }) {
  return (
    <ul className="-mx-2 divide-y">
      {rows.map((row) => (
        <ExpandableEvidenceRow key={row.key} workspaceId={workspaceId} row={row} />
      ))}
    </ul>
  );
}

function ExpandableEvidenceRow({
  workspaceId,
  row,
}: { workspaceId: string | undefined; row: EvidenceRowItem }) {
  const [open, setOpen] = React.useState(Boolean(row.defaultOpen));
  const q = useProjectMentions(row.entityKind, row.entityId, row.projectId, workspaceId, open);
  const toggle = React.useCallback(() => {
    // Read `open` from the closure rather than from a functional updater.
    // State updaters must be pure, and StrictMode double-invokes them in dev,
    // so a capture() in there double-counts under the verification hatch.
    if (!open) {
      // Only expansion counts. A collapse is not an activation signal.
      capture('evidence_expanded', {
        entity_type: row.entityKind,
        entity_id: row.entityId,
        project_id: row.projectId ?? null,
      });
    }
    setOpen(!open);
  }, [open, row.entityKind, row.entityId, row.projectId]);
  return (
    <li>
      {/* biome-ignore lint/a11y/useSemanticElements: row holds avatar and open links, so a button would nest interactives. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault();
            toggle();
          }
        }}
        className={cn(
          'group/row flex w-full cursor-pointer items-center gap-3 px-2 py-2.5 text-sm transition-colors hover:bg-muted/50 focus:outline-none focus-visible:bg-muted/50',
        )}
      >
        <div className="shrink-0">{row.avatar}</div>
        <div className="min-w-0 flex-1 text-left">
          <div className="truncate font-medium text-foreground">{row.title}</div>
          {row.subtitle ? (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{row.subtitle}</div>
          ) : null}
        </div>
        {row.meta ? <div className="shrink-0 text-xs text-muted-foreground">{row.meta}</div> : null}
        <Hint label={row.openLabel}>
          <button
            type="button"
            aria-label={row.openLabel}
            onClick={(e) => {
              e.stopPropagation();
              row.onOpen();
            }}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/row:opacity-100"
          >
            <row.openIcon className="h-4 w-4" />
          </button>
        </Hint>
        <ChevronDown
          aria-hidden
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </div>
      {open ? (
        <InlineEvidence
          entityName={row.entityName}
          enrichment={q.data?.enrichment ?? []}
          mentions={q.data?.mentions ?? []}
          isLoading={q.isLoading}
        />
      ) : null}
    </li>
  );
}
